import { Button, Modal, Tab, TabPanel, TabPanels, Tabs } from '@lokalise/louis';
import { useEffect, useState } from 'react';

import { useGetProjectsListQuery } from '@app/api/team';

import { AddExistingUsers, InviteNewUser, UserGroups } from './components';
import { TabListContainer } from './styles';
import type { AddContributorsModalProps } from './types';
import { AddContributorsModalPage } from './types';
import { extractTeamMembersEmails, getModalTitle } from './utils';

/**
 * Hook that controls adding and removing of specific query param into the URL without reloading page.
 * Useful when you want to control e.g. modal dialog visibility through URL.
 *
 * This hook should be eventually replaced with react-router.
 */
function useQueryParam({ param }: { param: string }) {
    const [url, setUrl] = useState(() => new URL(window.location.href));
    const isDefined = url.searchParams.has(param);

    const updateCurrentUrl = () => {
        setUrl(new URL(window.location.href));
    };

    const removeParam = () => {
        url.searchParams.delete(param);
        const newUrl = url.toString();
        // Use replace state - we don't want to open modal again on back button
        window.history.replaceState(null, 'remove-param', newUrl);

        // We need to update current url manually.
        // 'popstate' event is not called when changes to location are done from js code
        updateCurrentUrl();
    };

    const addParam = () => {
        url.searchParams.set(param, '1');
        const newUrl = url.toString();
        window.history.pushState(null, 'add-param', newUrl);

        // We need to update current url manually.
        // 'popstate' event is not called when changes to location are done from js code
        updateCurrentUrl();
    };

    useEffect(() => {
        window.addEventListener('popstate', updateCurrentUrl);
        window.addEventListener('hashchange', updateCurrentUrl);

        return () => {
            window.removeEventListener('popstate', updateCurrentUrl);
            window.removeEventListener('hashchange', updateCurrentUrl);
        };
    }, []);

    return {
        isDefined,
        addParam,
        removeParam,
    };
}

const AddContributorsModal = ({
    planName,
    teamName,
    page,
    seatsLimit,
    seatsTaken,
    invitedSeats,
    gracePeriodSeats,
    isExtraSeatsAllowed,
    isProviderAlpha,
    teamMembers,
    groups,
    activeProject,
    isGroupsEnabled,
}: AddContributorsModalProps) => {
    const { data: projects, isLoading: projectsLoading } = useGetProjectsListQuery(undefined, {
        skip: page === AddContributorsModalPage.project,
    });
    const [modalTitle, setModalTitle] = useState(getModalTitle(page));

    const {
        isDefined: isModalOpen,
        addParam: openModal,
        removeParam: closeModal,
    } = useQueryParam({
        // invite param is used in email invitations
        param: 'invite',
    });

    const renderModalContent = () => {
        switch (page) {
            case AddContributorsModalPage.team:
                return (
                    <InviteNewUser
                        planName={planName}
                        teamName={teamName}
                        page={AddContributorsModalPage.team}
                        projects={projects}
                        invitedSeats={invitedSeats}
                        seatsTaken={seatsTaken}
                        seatsLimit={seatsLimit}
                        gracePeriodSeats={gracePeriodSeats}
                        isProviderAlpha={isProviderAlpha}
                        isExtraSeatsAllowed={isExtraSeatsAllowed}
                        onModalTitleChange={setModalTitle}
                        onClose={closeModal}
                    />
                );

            case AddContributorsModalPage.project:
                return (
                    <Tabs>
                        <TabListContainer>
                            <Tab>Invite new user</Tab>
                            <Tab>Add existing users</Tab>
                            <Tab>User groups</Tab>
                        </TabListContainer>
                        <TabPanels>
                            <TabPanel>
                                {activeProject && (
                                    <InviteNewUser
                                        planName={planName}
                                        teamName={teamName}
                                        page={AddContributorsModalPage.project}
                                        activeProject={activeProject}
                                        invitedSeats={invitedSeats}
                                        seatsTaken={seatsTaken}
                                        seatsLimit={seatsLimit}
                                        gracePeriodSeats={gracePeriodSeats}
                                        isProviderAlpha={isProviderAlpha}
                                        isExtraSeatsAllowed={isExtraSeatsAllowed}
                                        onModalTitleChange={setModalTitle}
                                        onClose={closeModal}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel>
                                {teamMembers && activeProject && (
                                    <AddExistingUsers
                                        project={activeProject}
                                        teamMembers={extractTeamMembersEmails(teamMembers)}
                                        onClose={closeModal}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel>
                                {activeProject && groups && (
                                    <UserGroups
                                        projectId={activeProject?.id}
                                        groups={groups}
                                        isGroupsEnabled={isGroupsEnabled}
                                        onClose={closeModal}
                                    />
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                );

            default:
                return null;
        }
    };

    return (
        <>
            {page === AddContributorsModalPage.team && (
                <Button loading={projectsLoading} onClick={openModal} variant="primary">
                    Add team member
                </Button>
            )}
            <Modal opened={isModalOpen} onClose={closeModal} title={modalTitle}>
                {renderModalContent()}
            </Modal>
        </>
    );
};

export default AddContributorsModal;
